// API Client for Digitale Feuerwache
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API_BASE = `${BACKEND_URL}/api`;

// Flag to prevent multiple refresh attempts
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve();
    }
  });
  failedQueue = [];
};

// Create axios instance with credentials
const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Response interceptor for handling auth errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // Skip refresh logic for login and refresh endpoints
    if (originalRequest.url?.includes('/auth/login') || 
        originalRequest.url?.includes('/auth/refresh') ||
        originalRequest.url?.includes('/public/')) {
      return Promise.reject(error);
    }
    
    // If 401 and not already retrying
    if (error.response?.status === 401 && !originalRequest._retry) {
      // If already refreshing, queue this request
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(() => {
          return api(originalRequest);
        }).catch(err => {
          return Promise.reject(err);
        });
      }
      
      originalRequest._retry = true;
      isRefreshing = true;
      
      try {
        // Try to refresh the token
        await axios.post(`${API_BASE}/auth/refresh`, {}, {
          withCredentials: true
        });
        processQueue(null);
        return api(originalRequest);
      } catch (refreshError) {
        // Refresh failed - clear everything and redirect to login
        processQueue(refreshError);
        localStorage.removeItem('auth-storage'); // Clear Zustand persisted state
        
        // Only redirect if not already on login page
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
    
    return Promise.reject(error);
  }
);

// Auth API
export const authApi = {
  login: (data) => api.post('/auth/login', data),
  logout: () => api.post('/auth/logout'),
  refresh: () => api.post('/auth/refresh'),
  me: () => api.get('/auth/me'),
  changePassword: (data) => api.post('/auth/change-password', data),
};

// Setup API
export const setupApi = {
  getStatus: () => api.get('/setup/status'),
  initialSetup: (data) => api.post('/setup/initial-setup', data),
};

// Public API
export const publicApi = {
  getWacheConfig: (wacheId) => api.get('/public/wache-config', { params: { wache_id: wacheId } }),
  health: () => api.get('/public/health'),
};

// Users API
export const usersApi = {
  getAll: (params) => api.get('/users', { params }),
  // Alle Wache-User (über alle Wachabteilungen), z. B. für die Aushilfe-/Gast-Auswahl.
  // Nur für Rollen mit Einteilungs-Berechtigung erreichbar (Backend prüft).
  getAushilfeKandidaten: () => api.get('/users/aushilfe-kandidaten'),
  getMyPermissions: () => api.get('/rolle-permissions/mine'),
  getByAbteilung: () => api.get('/users/by-abteilung'),
  getPraktikantenbetreuer: () => api.get('/users/praktikantenbetreuer'),
  getById: (id) => api.get(`/users/${id}`),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.patch(`/users/${id}`, data),
  uploadAvatar: (id, formData) => api.post(`/users/${id}/avatar`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  moveAbteilung: (id, neueAbteilungId) => 
    api.post(`/users/${id}/move-abteilung`, null, { params: { neue_abteilung_id: neueAbteilungId } }),
  resetPassword: (id) => api.post(`/users/${id}/reset-password`),
  deactivate: (id) => api.delete(`/users/${id}`),
};

// Wache API
export const wacheApi = {
  get: () => api.get('/wache'),
  update: (data) => api.patch('/wache', data),
  getHelme: () => api.get('/wache/helme'),
  getAbteilungen: (params) => api.get('/wache/abteilungen', { params }),
  getAbteilung: (id) => api.get(`/wache/abteilungen/${id}`),
  createAbteilung: (data) => api.post('/wache/abteilungen', data),
  updateAbteilung: (id, data) => api.patch(`/wache/abteilungen/${id}`, data),
};

// Fahrzeuge API
export const fahrzeugeApi = {
  getAll: (params) => api.get('/fahrzeuge', { params }),
  getById: (id) => api.get(`/fahrzeuge/${id}`),
  create: (data) => api.post('/fahrzeuge', data),
  update: (id, data) => api.patch(`/fahrzeuge/${id}`, data),
  takel: (id, data) => api.post(`/fahrzeuge/${id}/takel`, data),
  getTakelHistorie: (id, params) => api.get(`/fahrzeuge/${id}/takel-historie`, { params }),
  getAllTakelLogs: (params) => api.get('/fahrzeuge/takel/alle', { params }),
  stornoTakel: (id, grund) => api.post(`/fahrzeuge/takel/${id}/storno`, null, { params: { storno_grund: grund } }),
};

// Einteilungen API
export const einteilungenApi = {
  getAll: (params) => api.get('/einteilungen', { params }),
  getMeine: (params) => api.get('/einteilungen/meine', { params }),
  getNaechsterDienst: (params) => api.get('/einteilungen/naechster-dienst', { params }),
  create: (data) => api.post('/einteilungen', data),
  createBulk: (data) => api.post('/einteilungen/bulk', data),
  update: (id, data) => api.patch(`/einteilungen/${id}`, data),
  delete: (id) => api.delete(`/einteilungen/${id}`),
  getFunktionen: (params) => api.get('/einteilungen/funktionen', { params }),
  createFunktion: (data) => api.post('/einteilungen/funktionen', data),
  updateFunktion: (id, data) => api.patch(`/einteilungen/funktionen/${id}`, data),
};

// Tags API
export const tagsApi = {
  getAll: (params) => api.get('/tags', { params }),
  create: (data) => api.post('/tags', data),
  update: (id, data) => api.patch(`/tags/${id}`, data),
  deactivate: (id) => api.delete(`/tags/${id}`),
};

// Permissions API
export const permissionsApi = {
  getAvailable: () => api.get('/permissions/available'),
  getEffective: () => api.get('/permissions/effective'),
};

// Statistiken API
export const statistikenApi = {
  getMitarbeiter: (id, params) => api.get(`/statistiken/mitarbeiter/${id}`, { params }),
  getMeine: (params) => api.get('/statistiken/meine', { params }),
  getAbteilung: (id, params) => api.get(`/statistiken/abteilung/${id}`, { params }),
  getUebersicht: (params) => api.get('/statistiken/uebersicht', { params }),
  exportCsv: (params) => api.get('/statistiken/export/csv', { params, responseType: 'blob' }),
};

// Developer API
export const developerApi = {
  // System Status
  getSystemStatus: () => api.get('/developer/system-status'),
  
  // Wache
  getWache: () => api.get('/developer/wache'),
  updateWache: (data) => api.patch('/developer/wache', data),
  
  // Users
  getUsers: () => api.get('/developer/users'),
  resetPassword: (userId) => api.post(`/developer/users/${userId}/reset-password`),
  setRole: (userId, rolle) => api.post(`/developer/users/${userId}/set-role`, null, { params: { rolle } }),
  
  // Tags
  getTags: () => api.get('/developer/tags'),
  createTag: (data) => api.post('/developer/tags', data),
  deleteTag: (tagId) => api.delete(`/developer/tags/${tagId}`),

  // DB-Wartung
  getDbStats: () => api.get('/developer/db/stats'),
  cleanupEinteilungen: (dryRun = true) => api.post(`/developer/db/cleanup-einteilungen?dry_run=${dryRun}`),
  
  // Fahrzeug Typen
  getFahrzeugTypen: () => api.get('/developer/fahrzeug-typen'),
  createFahrzeugTyp: (data) => api.post('/developer/fahrzeug-typen', data),
  uploadFahrzeugBild: (typ, formData) => api.post(`/developer/fahrzeug-typen/${typ}/upload-bild`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),

  // Helme (pro Wache)
  getHelme: () => api.get('/developer/helme'),
  uploadHelm: (kategorie, formData) => api.post(`/developer/helme/${kategorie}/upload`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  resetHelm: (kategorie) => api.delete(`/developer/helme/${kategorie}`),

  // Wache-Logo
  uploadWacheLogo: (formData) => api.post('/developer/wache-logo/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  resetWacheLogo: () => api.delete('/developer/wache-logo'),
  
  // Funktionen
  getFunktionen: () => api.get('/developer/funktionen'),
  createFunktion: (formData) => api.post('/developer/funktionen', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  deleteFunktion: (id) => api.delete(`/developer/funktionen/${id}`),
  
  // Abteilungen
  getAbteilungen: () => api.get('/developer/abteilungen'),
  createAbteilung: (formData) => api.post('/developer/abteilungen', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  deleteAbteilung: (id) => api.delete(`/developer/abteilungen/${id}`),
  
  // Audit Logs
  getAuditLogs: (limit = 100) => api.get('/developer/audit-logs', { params: { limit } }),

  // Statistik-Kategorien pro Fahrzeug
  getFahrzeugeKategorien: () => api.get('/developer/fahrzeuge-kategorien'),
  updateFahrzeugKategorie: (fahrzeugId, statistik_kategorie) =>
    api.patch(`/developer/fahrzeuge/${fahrzeugId}/kategorie`, { statistik_kategorie }),
};

// Dienst API (neue Einteilungs-API)
export const dienstApi = {
  getTagesuebersicht: () => api.get('/dienst/tagesuebersicht'),
  // Fahrzeugtypen (globale Bibliothek)
  getFahrzeugtypen: (params) => api.get('/dienst/fahrzeugtypen', { params }),
  getFahrzeugtypenBilder: () => api.get('/dienst/fahrzeugtypen-bilder'),
  getFahrzeugtyp: (id) => api.get(`/dienst/fahrzeugtypen/${id}`),
  seedFahrzeugtypen: () => api.post('/dienst/fahrzeugtypen/seed'),
  resetFahrzeugtypen: () => api.post('/dienst/fahrzeugtypen/reset'),
  
  // Standard-Funktionen (ATF, ATM, R1, etc.)
  getFunktionen: () => api.get('/dienst/funktionen'),
  
  // Wachen-Fahrzeuge
  getFahrzeuge: (params) => api.get('/dienst/fahrzeuge', { params }),
  getFahrzeug: (id) => api.get(`/dienst/fahrzeuge/${id}`),
  createFahrzeug: (data) => api.post('/dienst/fahrzeuge', data),
  updateFahrzeug: (id, data) => api.patch(`/dienst/fahrzeuge/${id}`, data),
  deleteFahrzeug: (id) => api.delete(`/dienst/fahrzeuge/${id}`),
  
  // Takel-Funktionen für Wachen-Fahrzeuge
  takel: (id, data) => api.post(`/dienst/fahrzeuge/${id}/takel`, data),
  getTakelHistorie: (id, params) => api.get(`/dienst/fahrzeuge/${id}/takel-historie`, { params }),
  
  // Diensteinteilungen
  getEinteilungen: (params) => api.get('/dienst/einteilungen', { params }),
  createEinteilung: (data) => api.post('/dienst/einteilungen', data),
  updateEinteilung: (id, data) => api.patch(`/dienst/einteilungen/${id}`, data),
  deleteEinteilung: (id) => api.delete(`/dienst/einteilungen/${id}`),
  
  // Sollstärke
  getSollstaerke: (params) => api.get('/dienst/sollstaerke', { params }),
  setSollstaerke: (data) => api.post('/dienst/sollstaerke', data),
  
  // Soll-Ist-Vergleich
  getSollIstVergleich: (params) => api.get('/dienst/soll-ist-vergleich', { params }),
  
  // Nächster Dienst (Magnetboard)
  getNaechsterDienst: (params) => api.get('/dienst/naechster-dienst', { params }),

  // Dienstplan Audit-Log (90 Tage)
  getDienstplanAuditLog: (params) => api.get('/dienst/audit-log', { params }),
  
  // Statistiken
  getMitarbeiterStatistik: (id, params) => api.get(`/dienst/statistik/mitarbeiter/${id}`, { params }),
  getMeineStatistik: (params) => api.get('/dienst/statistik/meine', { params }),
  
  // Dienstplan-Matrix (8-Tage-Rhythmus)
  getDiensttage: (params) => api.get('/dienst/diensttage', { params }),
  getMatrix: (params) => api.get('/dienst/plan/matrix', { params }),
  setStatus: (params) => api.post('/dienst/plan/status', null, { params }),
  setFunktion: (params) => api.post('/dienst/plan/funktion', null, { params }),
  // Neue kombinierte Funktion + Springer Zuweisung
  setFunktionMitSpringer: (data) => api.post('/dienst/plan/funktion-mit-springer', data),
  clearEinteilung: (params) => api.delete('/dienst/plan/einteilung', { params }),
  
  // Fahrzeugtypen (für Funktionen-Lookup)
  getFahrzeugtypen: () => api.get('/dienst/fahrzeugtypen'),
};

// Historischer Dienstplan-Importer
export const dienstImportApi = {
  analyze: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/dienst/plan/import/analyze', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  preview: (data) => api.post('/dienst/plan/import/preview', data),
  commit: (data) => api.post('/dienst/plan/import/commit', data),
  listBatches: () => api.get('/dienst/plan/import/batches'),
  deleteBatch: (batchId) => api.delete(`/dienst/plan/import/batches/${batchId}`),
};

// Fahrzeuge API (legacy endpoints)
export const fahrzeugApi = {
  // Fahrzeuge
  getFahrzeuge: (params) => api.get('/fahrzeuge', { params }),
  getFahrzeug: (id) => api.get(`/fahrzeuge/${id}`),
  createFahrzeug: (data) => api.post('/fahrzeuge', data),
  updateFahrzeug: (id, data) => api.patch(`/fahrzeuge/${id}`, data),
  deleteFahrzeug: (id) => api.delete(`/fahrzeuge/${id}`),
  
  // Takel operations
  takel: (id, data) => api.post(`/fahrzeuge/${id}/takel`, data),
  getTakelHistorie: (id, params) => api.get(`/fahrzeuge/${id}/takel-historie`, { params }),
  getAlleTakelLogs: (params) => api.get('/fahrzeuge/takel/alle', { params }),
  stornoTakel: (takelId, grund) => api.post(`/fahrzeuge/takel/${takelId}/storno`, { storno_grund: grund }),
  
  // Kommentare
  getKommentare: (id, params) => api.get(`/fahrzeuge/${id}/kommentare`, { params }),
  createKommentar: (id, data) => api.post(`/fahrzeuge/${id}/kommentare`, data),
  deleteKommentar: (fahrzeugId, kommentarId) => api.delete(`/fahrzeuge/${fahrzeugId}/kommentare/${kommentarId}`),
};

// Benachrichtigungen API
export const benachrichtigungApi = {
  getAll: (params) => api.get('/benachrichtigungen', { params }),
  getUnreadCount: () => api.get('/benachrichtigungen/count'),
  markAsRead: (id) => api.post(`/benachrichtigungen/${id}/gelesen`),
  markAllAsRead: () => api.post('/benachrichtigungen/alle-gelesen'),
  delete: (id) => api.delete(`/benachrichtigungen/${id}`),
};

// Dienstwünsche API
export const dienstwunschApi = {
  list: (params) => api.get('/dienstwuensche', { params }),
  create: (data, abteilungId) => api.post(`/dienstwuensche?abteilung_id=${abteilungId}`, data),
  update: (id, data) => api.patch(`/dienstwuensche/${id}`, data),
  remove: (id) => api.delete(`/dienstwuensche/${id}`),
};

// Atemschutzüberwachung (AEF) API
export const atemschutzApi = {
  // Quick Start - get pre-filled squad data from duty roster
  getQuickStart: (params) => api.get('/atemschutz/quick-start', { params }),
  
  // Session Management
  createSession: (data) => api.post('/atemschutz/sessions', data),
  getMyActiveSession: () => api.get('/atemschutz/sessions/active'),
  getAllActiveSessions: () => api.get('/atemschutz/sessions/active/all'),
  getSession: (id) => api.get(`/atemschutz/sessions/${id}`),
  finishSession: (id) => api.post(`/atemschutz/sessions/${id}/finish`),
  deleteSession: (id) => api.delete(`/atemschutz/sessions/${id}`),
  
  // Trupp (Squad) Management
  addTrupp: (sessionId, data) => api.post(`/atemschutz/sessions/${sessionId}/trupps`, data),
  updateTruppStatus: (sessionId, truppId, data) => 
    api.patch(`/atemschutz/sessions/${sessionId}/trupps/${truppId}/status`, data),
  toggleFluchthaube: (sessionId, truppId, aktiv) => 
    api.post(`/atemschutz/sessions/${sessionId}/trupps/${truppId}/fluchthaube`, { aktiv }),
  triggerMayday: (sessionId, truppId) => 
    api.post(`/atemschutz/sessions/${sessionId}/trupps/${truppId}/mayday`),
  
  // Warnings
  addWarnung: (sessionId, truppId, warnungTyp) => 
    api.post(`/atemschutz/sessions/${sessionId}/trupps/${truppId}/warnung`, null, { params: { warnung_typ: warnungTyp } }),
  confirmWarnung: (sessionId, truppId, data) => 
    api.post(`/atemschutz/sessions/${sessionId}/trupps/${truppId}/warnung/bestaetigen`, data),
  
  // History
  getHistory: (params) => api.get('/atemschutz/history', { params }),
};

// Feature Flags API
export const featureFlagsApi = {
  getActiveMenuKeys: () => api.get('/feature-flags/active-menu-keys'),
  getAll: () => api.get('/feature-flags'),
  create: (data) => api.post('/feature-flags', data),
  update: (menuKey, data) => api.patch(`/feature-flags/${menuKey}`, data),
  toggle: (menuKey) => api.post(`/feature-flags/${menuKey}/toggle`),
  delete: (menuKey) => api.delete(`/feature-flags/${menuKey}`),
  initialize: () => api.post('/feature-flags/initialize'),
};

// Pool-Kleidung API
export const poolKleidungApi = {
  getAll: (typ) => api.get('/pool-kleidung', { params: { typ } }),
  getById: (id) => api.get(`/pool-kleidung/${id}`),
  getHistorie: (id) => api.get(`/pool-kleidung/${id}/historie`),
  getStatistik: () => api.get('/pool-kleidung/statistik'),
  create: (data) => api.post('/pool-kleidung', data),
  update: (id, data) => api.patch(`/pool-kleidung/${id}`, data),
  delete: (id) => api.delete(`/pool-kleidung/${id}`),
  ausleihen: (id, data) => api.post(`/pool-kleidung/${id}/ausleihen`, data),
  zurueckgeben: (id, data) => api.post(`/pool-kleidung/${id}/zurueckgeben`, data),
};

// Praktikanten API
export const praktikantenApi = {
  getAll: (nurAktive) => api.get('/praktikanten', { params: { nur_aktive: nurAktive } }),
  getMeine: () => api.get('/praktikanten/meine'),
  getById: (id) => api.get(`/praktikanten/${id}`),
  create: (data) => api.post('/praktikanten', data),
  update: (id, data) => api.patch(`/praktikanten/${id}`, data),
  delete: (id) => api.delete(`/praktikanten/${id}`),
};

// Schrank-Management API
export const schrankApi = {
  // Permissions
  getMyPermissions: () => api.get('/schrank/me/permissions'),

  // Buildings
  listBuildings: () => api.get('/schrank/buildings'),
  createBuilding: (data) => api.post('/schrank/buildings', data),
  updateBuilding: (id, data) => api.patch(`/schrank/buildings/${id}`, data),
  deleteBuilding: (id) => api.delete(`/schrank/buildings/${id}`),

  // Floors
  listFloors: (buildingId) => api.get(`/schrank/buildings/${buildingId}/floors`),
  createFloor: (buildingId, data) => api.post(`/schrank/buildings/${buildingId}/floors`, data),
  updateFloor: (id, data) => api.patch(`/schrank/floors/${id}`, data),
  deleteFloor: (id) => api.delete(`/schrank/floors/${id}`),

  // Rooms
  listRooms: (floorId) => api.get(`/schrank/floors/${floorId}/rooms`),
  createRoom: (floorId, data) => api.post(`/schrank/floors/${floorId}/rooms`, data),
  updateRoom: (id, data) => api.patch(`/schrank/rooms/${id}`, data),
  deleteRoom: (id) => api.delete(`/schrank/rooms/${id}`),

  // Schränke
  listSchraenke: (params) => api.get('/schrank/schraenke', { params }),
  createSchrank: (roomId, data) => api.post(`/schrank/rooms/${roomId}/schraenke`, data),
  bulkCreateSchraenke: (roomId, data) => api.post(`/schrank/rooms/${roomId}/schraenke/bulk`, data),
  convertToContainer: (schrankId, data) => api.post(`/schrank/schraenke/${schrankId}/convert-to-container`, data),
  updateSchrank: (id, data) => api.patch(`/schrank/schraenke/${id}`, data),
  deleteSchrank: (id) => api.delete(`/schrank/schraenke/${id}`),

  // Assignments
  listAssignments: (params) => api.get('/schrank/assignments', { params }),
  assign: (schrankId, data) => api.post(`/schrank/schraenke/${schrankId}/assign`, data),
  endAssignment: (assignmentId) => api.post(`/schrank/assignments/${assignmentId}/end`),

  // Möbel
  listMoebel: (roomId) => api.get(`/schrank/rooms/${roomId}/moebel`),
  createMoebel: (roomId, data) => api.post(`/schrank/rooms/${roomId}/moebel`, data),
  updateMoebel: (id, data) => api.patch(`/schrank/moebel/${id}`, data),
  deleteMoebel: (id) => api.delete(`/schrank/moebel/${id}`),
};


// ==== Mitarbeiter-Kategorien (Developer verwaltet, ADMIN aufwärts weist zu) ====
export const kategorienApi = {
  list: (includeInactive = false) => api.get('/mitarbeiter-kategorien', { params: { include_inactive: includeInactive } }),
  getSachgebiete: (params) => api.get('/mitarbeiter-kategorien/sachgebiete', { params }),
  create: (data) => api.post('/mitarbeiter-kategorien', data),
  update: (id, data) => api.put(`/mitarbeiter-kategorien/${id}`, data),
  delete: (id) => api.delete(`/mitarbeiter-kategorien/${id}`),
};


// ==== Rolle-Permission-Matrix (Developer-only) ====
export const rollePermissionsApi = {
  getMatrix: () => api.get('/rolle-permissions'),
  updateMatrix: (entries) => api.put('/rolle-permissions', { entries }),
};


// ==== Benachrichtigungs-Zielgruppen (Developer-only) ====
// Pro Benachrichtigungstyp konfigurieren, welche Kategorien + Rollen die Benachrichtigung erhalten
export const benachrichtigungsZielgruppenApi = {
  getAll: () => api.get('/benachrichtigungs-zielgruppen'),
  upsert: (typ, data) => api.put(`/benachrichtigungs-zielgruppen/${typ}`, data),
  remove: (typ) => api.delete(`/benachrichtigungs-zielgruppen/${typ}`),
};


export default api;


export const sachgebieteVerwaltungApi = {
  list: () => api.get('/mitarbeiter-kategorien/verwaltung'),
  users: () => api.get('/mitarbeiter-kategorien/verwaltung/mitarbeiter'),
  create: data => api.post('/mitarbeiter-kategorien/verwaltung', data),
  update: (id, data) => api.put(`/mitarbeiter-kategorien/verwaltung/${id}`, data),
};
